import { useProjectBillingContext } from "~/hooks/useProjectBillingContext";
import { useWire } from "~/wire";

export const gateCreditsDescriptions = {
  prompt: "Create an account to keep using Storytell.",
  fileUpload: "Please top up your credits to upload more files",
};

// Deep Intelligence (research mode) bills at ~5x a normal prompt and a single run can
// consume tens of thousands of credits, so it is withheld below this floor rather than
// the >0 floor that gates ordinary prompts (see useGateActionByCredits).
const DEEP_INTELLIGENCE_MIN_CREDITS = 25000;

/**
 * Reports whether the active balance is too low to run Deep Intelligence. Mirrors the
 * project-vs-home balance split in useGateActionByCredits, and fails open while billing
 * is still loading so a slow fetch never blocks a user who can afford it.
 */
export const useDeepIntelligenceCreditGate = () => {
  const wire = useWire();
  const { data: projectBilling } = useProjectBillingContext(() => wire.currentProjectId());

  const belowThreshold = (): boolean => {
    if (wire.currentProjectId() !== undefined) {
      const projectCtx = projectBilling();
      return projectCtx !== undefined && projectCtx.balance <= DEEP_INTELLIGENCE_MIN_CREDITS;
    }
    if (!wire.billing.show() || wire.billing.loading()) return false;
    return wire.billing.credits() <= DEEP_INTELLIGENCE_MIN_CREDITS;
  };

  return { belowThreshold };
};

export const useGateActionByCredits = <T extends Array<unknown>, U>(
  description: () => string,
  action: (...args: T) => U,
  onCancel?: () => void
) => {
  const wire = useWire();
  // In a project, the paying org may differ from the caller's home org
  // (billing_mode=explicit), so gate on the project's balance, not credits().
  const { data: projectBilling } = useProjectBillingContext(() => wire.currentProjectId());

  const wrappedAction = (...args: T): U | undefined => {
    const projectCtx = projectBilling();
    const inProject = wire.currentProjectId() !== undefined;

    let shouldGate = false;
    if (inProject) {
      shouldGate = projectCtx !== undefined && projectCtx.balance <= 0;
    } else if (wire.billing.show() && !wire.billing.loading()) {
      shouldGate = wire.billing.credits() <= 0;
    }

    if (shouldGate) {
      wire.ui.creditsGate.openModal(description());
      onCancel?.();
      return;
    }
    return action(...args);
  };

  return wrappedAction;
};
